import { useState, useEffect, useRef } from 'react'

interface UseInactivityTimerOptions {
  timeout: number // in milliseconds
  onTimeout: () => void
}

export function useInactivityTimer({
  timeout,
  onTimeout,
}: UseInactivityTimerOptions) {
  const [remainingTime, setRemainingTime] = useState(timeout)
  const lastActivityRef = useRef<number>(Date.now())
  const hasTimedOutRef = useRef<boolean>(false)

  useEffect(() => {
    // Reset on mount
    lastActivityRef.current = Date.now()
    hasTimedOutRef.current = false

    // Countdown interval - runs every 100ms for smooth updates
    const countdownInterval = setInterval(() => {
      const now = Date.now()
      const elapsed = now - lastActivityRef.current
      const remaining = Math.max(0, timeout - elapsed)

      setRemainingTime(remaining)

      // Trigger timeout when reaching zero
      if (remaining === 0 && !hasTimedOutRef.current) {
        hasTimedOutRef.current = true
        onTimeout()
      }
    }, 100)

    // Activity handler with debouncing
    let debounceTimeout: NodeJS.Timeout | null = null

    const handleActivity = () => {
      // Debounce to avoid too frequent updates
      if (debounceTimeout) {
        clearTimeout(debounceTimeout)
      }

      debounceTimeout = setTimeout(() => {
        lastActivityRef.current = Date.now()
        hasTimedOutRef.current = false
      }, 50)
    }

    // Activity event listeners
    const events = [
      'mousemove',
      'mousedown',
      'keypress',
      'scroll',
      'touchstart',
    ]

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    // Cleanup
    return () => {
      clearInterval(countdownInterval)
      if (debounceTimeout) {
        clearTimeout(debounceTimeout)
      }
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
    }
  }, [timeout, onTimeout])

  return {
    remainingTime,
  }
}
